import { useMemo, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Search, Wand2, Pencil, Trash2, Link2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { useWords, useInvalidate } from "@/lib/queries";
import { WordDialog } from "@/components/language/WordDialog";
import { ExtractWordsDialog } from "@/components/language/ExtractWordsDialog";
import { WordSuggestions } from "@/components/language/WordSuggestions";
import { useGlyphs, GlyphSpelling } from "@/components/language/Script";
import { SafeImg } from "@/components/ImageGallery";

function WordRow({ w, canEdit, onEdit, onDelete, onOpenEntity, glyphs }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="group grid gap-1 border-b border-[#2D3442] py-4 last:border-0 sm:grid-cols-[220px_1fr_auto]" data-testid={`word-row-${w.id}`}>
      <div>
        <div className="font-display text-2xl text-[#F3EBDD]" data-testid={`word-text-${w.id}`}>{w.word}</div>
        {w.pronunciation && <div className="text-xs text-[#A8A090]">/{w.pronunciation}/</div>}
        <div className="mt-1"><GlyphSpelling ids={w.glyph_ids} glyphs={glyphs} /></div>
        {w.image_url && <SafeImg src={w.image_url} alt={w.word} className="mt-2 h-16 w-16 rounded" testid={`word-image-${w.id}`} />}
      </div>
      <div className="text-sm">
        {w.part_of_speech && <span className="mr-2 italic text-[#14B8A6]">{w.part_of_speech}</span>}
        <span className="text-[#E8DFD1]" data-testid={`word-meaning-${w.id}`}>{w.meaning || "—"}</span>
        {w.example && <div className="mt-1 italic text-[#A8A090]">“{w.example}”</div>}
        {w.notes && <div className="mt-1 text-xs text-[#A8A090]">{w.notes}</div>}
        {w.entity && <button onClick={() => onOpenEntity(w.entity.id)} className="mt-1 flex items-center gap-1 text-xs text-[#A8A090] hover:text-[#F59E0B]" data-testid={`word-entity-${w.id}`}><Link2 className="h-3 w-3" />{w.entity.name}</button>}
      </div>
      {canEdit && (
        <div className="flex gap-1 opacity-60 transition-opacity group-hover:opacity-100">
          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={onEdit} data-testid={`word-edit-${w.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={onDelete} data-testid={`word-delete-${w.id}`}><Trash2 className="h-3.5 w-3.5 text-red-400" /></Button>
        </div>
      )}
    </motion.div>
  );
}

export function Dictionary({ lang, canEdit }) {
  const { base } = useOutletContext();
  const navigate = useNavigate();
  const invalidate = useInvalidate();
  const { data: words = [] } = useWords(lang.id);
  const { data: glyphs = [] } = useGlyphs(lang.id);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const groups = useMemo(() => {
    const s = q.toLowerCase();
    const shown = words.filter((w) => !s || [w.word, w.meaning, w.notes, w.example].some((x) => (x || "").toLowerCase().includes(s)));
    const out = {};
    shown.forEach((w) => { const L = (w.word[0] || "#").toUpperCase(); (out[L] = out[L] || []).push(w); });
    return Object.entries(out).sort(([a], [b]) => a.localeCompare(b));
  }, [words, q]);
  const remove = async (w) => {
    await api.delete(`/words/${w.id}`);
    invalidate(["words", lang.id], ["language", lang.id], ["languages", lang.universe_id]);
  };

  return (
    <div data-testid="dictionary">
      {canEdit && <WordSuggestions lang={lang} />}
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#A8A090]" />
          <Input className="pl-9" placeholder="Search words or meanings…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="dictionary-search" />
        </div>
        {canEdit && (
          <div className="ml-auto flex gap-2">
            <Button variant="outline" onClick={() => setExtracting(true)} data-testid="extract-words-button"><Wand2 className="h-4 w-4 mr-1.5" />Find words in a note</Button>
            <Button onClick={() => setEditing({})} data-testid="add-word-button"><Plus className="h-4 w-4 mr-1" />Add word</Button>
          </div>
        )}
      </div>
      {groups.length === 0 ? (
        <div className="rounded-md border border-dashed border-[#2D3442] p-12 text-center" data-testid="dictionary-empty">
          <p className="font-display text-3xl">{q ? "No matching words." : "The glossary is empty."}</p>
          {!q && <p className="mt-2 text-sm text-[#A8A090]">Add words by hand, or let the scribe find them in a note.</p>}
        </div>
      ) : groups.map(([letter, list]) => (
        <section key={letter} className="mb-6" data-testid={`dictionary-letter-${letter}`}>
          <div className="mb-1 font-display text-3xl text-[#14B8A6]">{letter}</div>
          <div className="rounded-md border border-[#2D3442] bg-[#13171F] px-5">
            {list.map((w) => <WordRow key={w.id} w={w} glyphs={glyphs} canEdit={canEdit} onEdit={() => setEditing(w)} onDelete={() => remove(w)} onOpenEntity={(id) => navigate(`${base}/lore/${id}`)} />)}
          </div>
        </section>
      ))}
      {editing && <WordDialog lang={lang} initial={editing.id ? editing : null} onOpenChange={() => setEditing(null)} />}
      {extracting && <ExtractWordsDialog lang={lang} onOpenChange={setExtracting} />}
    </div>
  );
}
